import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SmtpSettings } from "@/components/SmtpSettings";
import { toast } from "sonner";
import { Loader2, Save, Settings as SettingsIcon, Plus, Trash2 } from "lucide-react";

const EMAIL_TYPES = [
  { key: "email_planning_published", label: "Planning gepubliceerd", desc: "Bericht wanneer de planning (voorlopig of definitief) wordt gedeeld." },
  { key: "email_shift_changed", label: "Dienstwijzigingen", desc: "Bericht wanneer een dienst wijzigt of vervalt." },
  { key: "email_services_available", label: "Nieuwe diensten beschikbaar", desc: "Bericht wanneer nieuwe diensten openstaan om beschikbaarheid voor op te geven." },
  { key: "email_availability_reminder", label: "Herinnering beschikbaarheid", desc: "Herinnering aan wie nog geen beschikbaarheid heeft doorgegeven." },
  { key: "email_swap", label: "Ruilverzoeken", desc: "Berichten rond ruilverzoeken (aanvraag, goedkeuring, afwijzing)." },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([api.get("/admin/settings"), api.get("/admin/service-types")]);
      setSettings(s.data); setTypes(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setNum = (k) => (e) => setSettings((s) => ({ ...s, [k]: parseInt(e.target.value, 10) || 0 }));
  const setBool = (k) => (v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", settings); toast.success("Instellingen opgeslagen"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addType = async () => {
    if (!newType.trim()) return;
    try { await api.post("/admin/service-types", { name: newType.trim() }); setNewType(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delType = async (id) => {
    try { await api.delete(`/admin/service-types/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !settings) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><SettingsIcon className="w-7 h-7 text-red-600" /> Instellingen</h1>
        <p className="text-muted-foreground mt-1">Grenswaarden voor waarschuwingen en planningsregels.</p>
      </div>

      <SmtpSettings />
      <Card className="p-6 space-y-5">
        <h3 className="font-heading font-semibold">Verdeling & waarschuwingen</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Waarschuwing onder (% van gemiddelde)</Label><Input type="number" value={settings.warning_under_pct} onChange={setNum("warning_under_pct")} data-testid="setting-under" /></div>
          <div className="space-y-1.5"><Label>Waarschuwing boven (% van gemiddelde)</Label><Input type="number" value={settings.warning_over_pct} onChange={setNum("warning_over_pct")} data-testid="setting-over" /></div>
          <div className="space-y-1.5"><Label>Minimale rusttijd (uren)</Label><Input type="number" value={settings.rest_hours} onChange={setNum("rest_hours")} data-testid="setting-rest" /></div>
          <div className="space-y-1.5"><Label>Max. diensten per vrijwilliger per kalendermaand (0 = geen)</Label><Input type="number" value={settings.max_shifts} onChange={setNum("max_shifts")} data-testid="setting-max" /></div>
        </div>
        <div className="flex items-center justify-between border rounded-lg p-4">
          <div>
            <p className="font-medium text-sm">Ruilverzoeken automatisch verwerken</p>
            <p className="text-xs text-muted-foreground">Uit = planner moet elke ruil goedkeuren (aanbevolen).</p>
          </div>
          <Switch checked={settings.auto_approve_swaps} onCheckedChange={(v) => setSettings((s) => ({ ...s, auto_approve_swaps: v }))} data-testid="setting-auto-swap" />
        </div>
        <Button onClick={save} disabled={busy} data-testid="settings-save">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Opslaan
        </Button>
      </Card>

      <Card className="p-6 space-y-4" data-testid="email-settings-card">
        <div>
          <h3 className="font-heading font-semibold">E-mailmeldingen</h3>
          <p className="text-xs text-muted-foreground mt-1">Bepaal welke meldingen óók per e-mail verstuurd worden. In-app meldingen blijven altijd werken. Uitnodigingen worden altijd gemaild.</p>
        </div>
        <div className="flex items-center justify-between border rounded-lg p-4">
          <div>
            <p className="font-medium text-sm">E-mail versturen</p>
            <p className="text-xs text-muted-foreground">Hoofdschakelaar — uit = er worden geen meldingsmails verstuurd.</p>
          </div>
          <Switch checked={settings.email_enabled ?? true} onCheckedChange={setBool("email_enabled")} data-testid="email-enabled" />
        </div>
        <div className={`space-y-2 ${settings.email_enabled === false ? "opacity-50 pointer-events-none" : ""}`}>
          {EMAIL_TYPES.map((t) => (
            <div key={t.key} className="flex items-center justify-between border rounded-lg p-3">
              <div><p className="font-medium text-sm">{t.label}</p><p className="text-xs text-muted-foreground">{t.desc}</p></div>
              <Switch checked={settings[t.key] ?? true} onCheckedChange={setBool(t.key)} data-testid={`email-type-${t.key}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} data-testid="email-settings-save">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Opslaan
        </Button>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-semibold">Werkzaamheden (types)</h3>
        <div className="flex flex-wrap gap-2">
          {types.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-full text-sm font-medium" data-testid={`type-${t.id}`}>
              {t.name}
              <button onClick={() => delType(t.id)} className="hover:text-rose-600" data-testid={`del-type-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-sm">
          <Input placeholder="Nieuwe werkzaamheid" value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="new-type-input" />
          <Button onClick={addType} variant="outline" data-testid="add-type-btn"><Plus className="w-4 h-4" /></Button>
        </div>
      </Card>
    </div>
  );
}
